'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type FormEvent, type RefObject } from 'react';
export interface PreQualifyGateContact {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}
export const PREQUALIFY_GATE_HEADING_ID = 'prequal-gate-heading';
export const PREQUALIFY_GATE_DESCRIPTION_ID = 'prequal-gate-description';
type FieldKey = 'firstName' | 'lastName' | 'email' | 'phone';
const FIELD_ORDER: FieldKey[] = ['firstName', 'lastName', 'email', 'phone'];
const DEFAULT_DESCRIPTION = "Share your contact details so THIScar can help with financing, even if you don’t finish your application. Next, continue to the pre-qualification form.";
interface PreQualifyGateProps {
  /** Vehicle context, shown so the shopper knows what they are applying for. */
  vehicleLabel?: string | undefined;
  /** Copy under the heading. Say where the shopper goes next. */
  description?: string | undefined;
  submitLabel?: string | undefined;
  /**
   * Drop the card border, padding and shadow so a surrounding dialog can
   * supply the chrome instead.
   */
  frameless?: boolean | undefined;
  /** Lets a parent dialog move focus to the first field on open. */
  initialFocusRef?: RefObject<HTMLInputElement | null> | undefined;
  onSubmit: (contact: PreQualifyGateContact) => void;
  isSubmitting?: boolean;
  initialContact?: PreQualifyGateContact | undefined;
  onContactChange?: ((contact: PreQualifyGateContact) => void) | undefined;
  onInteraction?: (() => void) | undefined;
  /** Receives every invalid field, in form order. */
  onValidationError?: ((fields: FieldKey[]) => void) | undefined;
}

/**
 * First-party contact capture shown before the third-party credit widget.
 *
 * Everything the shopper types inside Capital One's iframe is invisible to us,
 * so a pre-qualify intent recorded without this carries no identity at all —
 * it can only ever be linked back to the resulting CRM lead by guessing from
 * VIN and timing. Contact fields let us compare identity when matching.
 *
 * The ask is framed honestly: we say what we will do with it, and we do not
 * pretend the third party requires it.
 */
export function PreQualifyGate(t0) {
  const $ = _c(72);
  const {
    vehicleLabel,
    description: t1,
    submitLabel: t2,
    frameless: t3,
    initialFocusRef,
    onSubmit,
    initialContact,
    onContactChange,
    isSubmitting: t4,
    onInteraction,
    onValidationError
  } = t0;
  const description = t1 === undefined ? DEFAULT_DESCRIPTION : t1;
  const submitLabel = t2 === undefined ? "Continue to pre-qualification" : t2;
  const frameless = t3 === undefined ? false : t3;
  const isSubmitting = t4 === undefined ? false : t4;
  const [firstName, setFirstName] = useState(initialContact?.firstName ?? "");
  const [lastName, setLastName] = useState(initialContact?.lastName ?? "");
  const [email, setEmail] = useState(initialContact?.email ?? "");
  const [phone, setPhone] = useState(initialContact?.phone ?? "");
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {};
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const [errors, setErrors] = useState(t5);
  let t6;
  let t7;
  if ($[1] !== email || $[2] !== firstName || $[3] !== lastName || $[4] !== onContactChange || $[5] !== phone) {
    t6 = () => {
      onContactChange?.({
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        email,
        phone
      });
    };
    t7 = [firstName, lastName, email, phone, onContactChange];
    $[1] = email;
    $[2] = firstName;
    $[3] = lastName;
    $[4] = onContactChange;
    $[5] = phone;
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  useEffect(t6, t7);
  let t8;
  if ($[8] !== email || $[9] !== firstName || $[10] !== lastName || $[11] !== phone) {
    t8 = () => {
      const next = {};
      if (!firstName.trim()) {
        next.firstName = "Please enter your first name";
      }
      if (!lastName.trim()) {
        next.lastName = "Please enter your last name";
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
        next.email = "Please enter a valid email address";
      }
      if (phone.replace(/\D/g, "").length < 10) {
        next.phone = "Please enter a 10-digit phone number";
      }
      setErrors(next);
      return FIELD_ORDER.filter(key => next[key]);
    };
    $[8] = email;
    $[9] = firstName;
    $[10] = lastName;
    $[11] = phone;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const validate = t8;
  let t9;
  if ($[13] !== email || $[14] !== firstName || $[15] !== isSubmitting || $[16] !== lastName || $[17] !== onInteraction || $[18] !== onSubmit || $[19] !== onValidationError || $[20] !== phone || $[21] !== validate) {
    t9 = event => {
      event.preventDefault();
      if (isSubmitting) {
        return;
      }
      onInteraction?.();
      const invalid = validate();
      const firstInvalid = invalid[0];
      if (firstInvalid) {
        onValidationError?.(invalid);
        event.currentTarget.querySelector(`#prequal-${firstInvalid}`)?.focus();
        return;
      }
      onSubmit({
        name: `${firstName.trim()} ${lastName.trim()}`,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim()
      });
    };
    $[13] = email;
    $[14] = firstName;
    $[15] = isSubmitting;
    $[16] = lastName;
    $[17] = onInteraction;
    $[18] = onSubmit;
    $[19] = onValidationError;
    $[20] = phone;
    $[21] = validate;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleSubmit = t9;
  const fieldClass = _temp;
  const t10 = frameless ? "t-w-full" : "t-mx-auto t-max-w-md t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-6 t-shadow-sm";
  const t11 = vehicleLabel ? ` for your ${vehicleLabel}` : "";
  let t12;
  if ($[23] !== t11) {
    t12 = <h1 id={PREQUALIFY_GATE_HEADING_ID} className="t-mb-1 t-text-xl t-font-semibold t-text-brand-ink">Get pre-qualified{t11}</h1>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== description) {
    t13 = <p id={PREQUALIFY_GATE_DESCRIPTION_ID} className="t-mb-5 t-text-sm t-text-brand-ink-muted">{description}</p>;
    $[25] = description;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = ["firstName", "lastName"];
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== errors || $[29] !== firstName || $[30] !== initialFocusRef || $[31] !== lastName) {
    t15 = (t14 as const).map(field => <div className="t-mb-4" key={field}><label htmlFor={`prequal-${field}`} className="t-mb-1 t-block t-text-sm t-font-medium t-text-brand-ink">{field === "firstName" ? "First name" : "Last name"}</label><input ref={field === "firstName" ? initialFocusRef : undefined} id={`prequal-${field}`} name={field} type="text" autoComplete={field === "firstName" ? "given-name" : "family-name"} required={true} maxLength={100} value={field === "firstName" ? firstName : lastName} onChange={e => (field === "firstName" ? setFirstName : setLastName)(e.target.value)} className={fieldClass(Boolean(errors[field]))} aria-invalid={Boolean(errors[field])} aria-describedby={errors[field] ? `prequal-${field}-error` : undefined} />{errors[field] && <p id={`prequal-${field}-error`} role="alert" className="t-mt-1 t-text-xs t-text-brand-rose">{errors[field]}</p>}</div>);
    $[28] = errors;
    $[29] = firstName;
    $[30] = initialFocusRef;
    $[31] = lastName;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <label htmlFor="prequal-email" className="t-mb-1 t-block t-text-sm t-font-medium t-text-brand-ink">Email</label>;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e_0 => setEmail(e_0.target.value);
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = fieldClass(Boolean(errors.email));
  const t19 = Boolean(errors.email);
  const t20 = errors.email ? "prequal-email-error" : undefined;
  let t21;
  if ($[35] !== email || $[36] !== t18 || $[37] !== t19 || $[38] !== t20) {
    t21 = <input id="prequal-email" name="email" type="email" autoComplete="email" inputMode="email" required={true} value={email} onChange={t17} className={t18} aria-invalid={t19} aria-describedby={t20} />;
    $[35] = email;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== errors.email) {
    t22 = errors.email && <p id="prequal-email-error" role="alert" className="t-mt-1 t-text-xs t-text-brand-rose">{errors.email}</p>;
    $[40] = errors.email;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t21 || $[43] !== t22) {
    t23 = <div className="t-mb-4">{t16}{t21}{t22}</div>;
    $[42] = t21;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <label htmlFor="prequal-phone" className="t-mb-1 t-block t-text-sm t-font-medium t-text-brand-ink">Phone</label>;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = e_1 => setPhone(e_1.target.value);
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  const t26 = fieldClass(Boolean(errors.phone));
  const t27 = Boolean(errors.phone);
  const t28 = errors.phone ? "prequal-phone-error" : undefined;
  let t29;
  if ($[47] !== phone || $[48] !== t26 || $[49] !== t27 || $[50] !== t28) {
    t29 = <input id="prequal-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" required={true} value={phone} onChange={t25} className={t26} aria-invalid={t27} aria-describedby={t28} />;
    $[47] = phone;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] !== errors.phone) {
    t30 = errors.phone && <p id="prequal-phone-error" role="alert" className="t-mt-1 t-text-xs t-text-brand-rose">{errors.phone}</p>;
    $[52] = errors.phone;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== t29 || $[55] !== t30) {
    t31 = <div className="t-mb-5">{t24}{t29}{t30}</div>;
    $[54] = t29;
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  const t32 = isSubmitting ? "One moment\u2026" : submitLabel;
  let t33;
  if ($[57] !== isSubmitting || $[58] !== t32) {
    t33 = <button type="submit" disabled={isSubmitting} className="t-inline-flex t-min-h-[48px] t-w-full t-items-center t-justify-center t-rounded-full t-bg-brand-ink t-px-8 t-font-semibold t-text-white t-transition-colors hover:t-bg-brand-violet focus:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-brand-violet focus-visible:t-ring-offset-2 disabled:t-opacity-60 motion-reduce:t-transition-none">{t32}</button>;
    $[57] = isSubmitting;
    $[58] = t32;
    $[59] = t33;
  } else {
    t33 = $[59];
  }
  let t34;
  if ($[60] !== handleSubmit || $[61] !== onInteraction || $[62] !== t15 || $[63] !== t23 || $[64] !== t31 || $[65] !== t33) {
    t34 = <form name="financing_contact" onSubmit={handleSubmit} onInput={onInteraction} noValidate={true}>{t15}{t23}{t31}{t33}</form>;
    $[60] = handleSubmit;
    $[61] = onInteraction;
    $[62] = t15;
    $[63] = t23;
    $[64] = t31;
    $[65] = t33;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] !== t10 || $[68] !== t12 || $[69] !== t13 || $[70] !== t34) {
    t35 = <div className={t10}>{t12}{t13}{t34}</div>;
    $[67] = t10;
    $[68] = t12;
    $[69] = t13;
    $[70] = t34;
    $[71] = t35;
  } else {
    t35 = $[71];
  }
  return t35;
}
function _temp(hasError) {
  return `t-w-full t-rounded-lg t-border t-px-3 t-py-2.5 t-text-base t-text-brand-ink focus:t-outline-none focus:t-ring-2 focus:t-ring-brand-violet focus:t-border-brand-violet ${hasError ? "t-border-brand-rose" : "t-border-gray-300"}`;
}